//---------------------
// 보험
//---------------------
import store from "@/store";
import { fnIsBalanceUndecidedEnabled } from "@/utils/balanceUndecidedGate";

// FA(안심 매매 패키지) 1차 오픈 플래그. false=내부 테스터만. 정식 오픈 시 true 로 변경.
// 서버(api-public initFirstamerican)도 동일 게이트(이메일 명단) — 결제 하드 차단.
// ⚠️ FA 풀오픈 = 게이트 3곳 동반: 이 라우터(firstamGuard) + FE firstamAccessHelpers + BE orderApp.
const FIRSTAM_PUBLIC_OPEN = true; // 2026-06-17 정식 오픈(firstamGuard 비테스터 차단 해제).

// 퍼스트아메리칸 퍼널 진입 가드(로그인 필요 + 1차 오픈은 내부 테스터만). 랜딩은 비로그인도 노출(가드 미적용).
// 비로그인 → 로그인(state=firstam, 로그인 후 AnalyzeLogin 이 step-1 로 이어줌).
// 이메일 미로드(하드 진입/persist 미복원)면 유저 정보 로드 후 판정 — 빈 이메일=비테스터로 차단.
// (이전 `email && ...` 조건은 이메일 빈값이면 게이트가 통째로 skip 되는 구멍이었음)
const fnStoredEmail = () => {
  const e = store.state && store.state.userStore && store.state.userStore._email;
  return e && String(e).trim() ? String(e).trim() : "";
};

// 이메일 기준 게이트(테스터 판정)를 쓰기 전에 반드시 호출한다. 하드 진입·persist 미복원이면
// 이메일이 비어 있어 테스터가 비테스터로 판정된다 — 화면마다 조용히 구 동작으로 새는 원인.
const fnEnsureEmailLoaded = async () => {
  if (fnStoredEmail()) return;
  try {
    await store.dispatch("userStore/acUserName"); // 이메일 포함 유저 정보 로드
  } catch (e) {
    /* 로드 실패 → 호출부에서 비테스터로 판정(fail closed) */
  }
};

const firstamGuard = async (to, from, next) => {
  if (!localStorage.naezip_to_ac_se) {
    next({ path: "/login", query: { state: "firstam" } });
    return;
  }
  // 풀오픈 상태여도 이메일은 채워 둔다 — 퍼널 내부의 잔금일 미정 게이트(NZSC-3814/3816)가
  // 같은 이메일 명단을 보기 때문. 이미 있으면 API 호출 없음.
  await fnEnsureEmailLoaded();
  if (FIRSTAM_PUBLIC_OPEN) {
    next();
    return;
  }
  if (!store.getters["userStore/isInternalTester"]) {
    next({ path: "/" });
    return;
  }
  next();
};

// NZSC-3814/3816 — 잔금일 미정 기획 전용 화면(구간 선택·계약 정보 입력) 진입 가드.
// 게이트가 꺼진 고객은 이 화면 자체가 없어야 한다(URL 직접 진입 포함).
const balanceUndecidedGuard = async (to, from, next) => {
  if (!localStorage.naezip_to_ac_se) {
    next({ path: "/login", query: { state: "firstam" } });
    return;
  }
  await fnEnsureEmailLoaded();
  if (!fnIsBalanceUndecidedEnabled(store.getters["userStore/isInternalTester"])) {
    next({ path: "/" });
    return;
  }
  next();
};

const insuranceRouter = [
  {
    path: "/insurance/lotte/legal-001/register/init",
    name: "insurance-lotte-legal-001-register-init",
    component: () =>
      import("../views/insurance/lotte/legal001/register/InsuranceLotteRegisterInitLegal001.vue"),
    beforeEnter: (to, from, next) => {
      if (localStorage.naezip_to_ac_se) {
        next();
      } else {
        next("/");
      }
    },
  },
  {
    path: "/insurance/lotte/legal-001/register/date",
    name: "insurance-lotte-legal001-register-date",
    component: () =>
      import("../views/insurance/lotte/legal001/register/InsuranceLotteRegisterDateLegal001.vue"),
    beforeEnter: (to, from, next) => {
      if (localStorage.naezip_to_ac_se) {
        next();
      } else {
        next("/");
      }
    },
  },
  {
    path: "/insurance/lotte/legal-001/register/terms",
    name: "insurance-lotte-legal-001-register-terms",
    component: () =>
      import("../views/insurance/lotte/legal001/register/InsuranceLotteRegisterTermsLegal001.vue"),
    beforeEnter: (to, from, next) => {
      if (localStorage.naezip_to_ac_se) {
        next();
      } else {
        next("/");
      }
    },
  },
  {
    path: "/insurance/lotte/legal-001/register/amount",
    name: "insurance-lotte-legal-001-register-amount",
    component: () =>
      import(
        "../views/insurance/lotte/legal001/register/amount/InsuranceLotteRegisterAmountMonthlyLegal001.vue"
      ),
    beforeEnter: (to, from, next) => {
      if (localStorage.naezip_to_ac_se) {
        next();
      } else {
        next("/");
      }
    },
  },
  {
    path: "/insurance/lotte/legal-001/register/under-review",
    name: "insurance-lotte-legal-001-register-under-review",
    component: () =>
      import(
        "../views/insurance/lotte/legal001/register/InsuranceLotteRegisterUnderReviewLegal001.vue"
      ),
    beforeEnter: (to, from, next) => {
      if (localStorage.naezip_to_ac_se) {
        next();
      } else {
        next("/");
      }
    },
  },
  {
    path: "/insurance/lotte/legal-001/register/under-review-free-month",
    name: "insurance-lotte-legal-001-register-under-review-free-month",
    component: () =>
      import(
        "../views/insurance/lotte/legal001/register/InsuranceLotteRegisterUnderReviewFreeMonthLegal001.vue"
      ),
    beforeEnter: (to, from, next) => {
      if (localStorage.naezip_to_ac_se) {
        next();
      } else {
        next("/");
      }
    },
  },
  {
    path: "/insurance/lotte/legal-001/register/under-review/date-missing/:insurance_seq",
    name: "insurance-lotte-legal-001-register-under-review-date-missing",
    component: () =>
      import(
        "../views/insurance/lotte/legal001/register/InsuranceLotteRegisterUnderReviewDateMissingLegal001.vue"
      ),
    beforeEnter: (to, from, next) => {
      if (localStorage.naezip_to_ac_se) {
        next();
      } else {
        next("/");
      }
    },
  },
  {
    path: "/insurance/lotte/legal-001/register/date-missing/:insurance_seq",
    name: "insurance-lotte-legal-001-register-date-missing",
    component: () =>
      import(
        "../views/insurance/lotte/legal001/register/InsuranceLotteRegisterDateMissingLegal001.vue"
      ),
    beforeEnter: (to, from, next) => {
      if (localStorage.naezip_to_ac_se) {
        next();
      } else {
        next("/");
      }
    },
  },

  //공통
  {
    path: "/insurance/payment/complete",
    name: "insurance-payment-complete",
    component: () => import("../views/insurance/common/InsurancePaymentComplete.vue"),
    beforeEnter: (to, from, next) => {
      if (localStorage.naezip_to_ac_se) {
        next();
      } else {
        next("/");
      }
    },
  },
  {
    path: "/insurance/lotte/legal-001/landing",
    name: "insurance-lotte-legal-001-landing",
    component: () => import("../views/insurance/lotte/legal001/InsuranceLotteLandingLegal001.vue"),
  },
  {
    path: "/insurance/address/choose",
    name: "insurance-address-choose",
    component: () => import("../views/insurance/common/InsuranceAddressChoose.vue"),
  },

  //---------------------
  // 퍼스트아메리칸 권원보험 (NZSC-3189)
  // 랜딩 → 가입 퍼널 7단계(숫자 인디케이터). 단계별 화면은 시안 확정 순서대로 추가.
  // (구 placeholder notice/property/info/terms/confirm 라우트는 7단계 완성 후 정리)
  //---------------------
  // 법률 상담 받기 안내(로톡) — 등기변동 알리미 '법률 상담받기'에서 진입
  {
    path: "/insurance/firstamerican/title-001/lawtalk",
    name: "insurance-firstamerican-title-001-lawtalk",
    component: () => import("../views/insurance/firstamerican/InsuranceFirstamericanLawtalk.vue"),
    beforeEnter: firstamGuard,
  },
  // 가입 정보 확인하기(심사 중) — 안심 보장 현황 '가입 정보 확인하기'에서 진입
  {
    path: "/insurance/firstamerican/title-001/signup-info",
    name: "insurance-firstamerican-title-001-signup-info",
    component: () =>
      import("../views/insurance/firstamerican/InsuranceFirstamericanSignupInfo.vue"),
    beforeEnter: firstamGuard,
  },
  // 부동산 정보 변경 — 변경할 주소지 선택(등기부→행안부, 신청 퍼널 Step3 재사용)
  {
    path: "/insurance/firstamerican/title-001/change/address",
    name: "insurance-firstamerican-title-001-change-address",
    component: () =>
      import("../views/insurance/firstamerican/InsuranceFirstamericanChangeAddress.vue"),
    beforeEnter: firstamGuard,
  },
  // 고객센터 채팅 — 가입 정보 확인 등 '고객센터 문의하기'에서 진입
  {
    path: "/insurance/firstamerican/title-001/customer-center",
    name: "insurance-firstamerican-title-001-customer-center",
    component: () =>
      import("../views/insurance/firstamerican/InsuranceFirstamericanCustomerCenter.vue"),
    beforeEnter: firstamGuard,
  },
  // 사고 접수 안내 — 가입 정보 확인(증권 발행 이후) '사고 접수하기'에서 진입
  {
    path: "/insurance/firstamerican/title-001/claim",
    name: "insurance-firstamerican-title-001-claim",
    component: () =>
      import("../views/insurance/firstamerican/InsuranceFirstamericanClaimGuide.vue"),
    beforeEnter: firstamGuard,
  },
  // 부동산 정보 변경 — 변경할 금액(거래가액) 입력
  {
    path: "/insurance/firstamerican/title-001/change/amount",
    name: "insurance-firstamerican-title-001-change-amount",
    component: () =>
      import("../views/insurance/firstamerican/InsuranceFirstamericanChangeAmount.vue"),
    beforeEnter: firstamGuard,
  },
  // 부동산 정보 변경 — 잔금일 변경(동일 UI, mode 분기)
  {
    path: "/insurance/firstamerican/title-001/change/balance-date",
    name: "insurance-firstamerican-title-001-change-balance-date",
    component: () =>
      import("../views/insurance/firstamerican/InsuranceFirstamericanChangeDate.vue"),
    props: { mode: "balance" },
    beforeEnter: firstamGuard,
  },
  // 부동산 정보 변경 — 계약체결일 변경(동일 UI, mode 분기)
  {
    path: "/insurance/firstamerican/title-001/change/contract-date",
    name: "insurance-firstamerican-title-001-change-contract-date",
    component: () =>
      import("../views/insurance/firstamerican/InsuranceFirstamericanChangeDate.vue"),
    props: { mode: "contract" },
    beforeEnter: firstamGuard,
  },
  // 부동산 정보 변경 — 변경 사유 입력 → 변경 신청 완료 팝업
  {
    path: "/insurance/firstamerican/title-001/change/reason",
    name: "insurance-firstamerican-title-001-change-reason",
    component: () =>
      import("../views/insurance/firstamerican/InsuranceFirstamericanChangeReason.vue"),
    beforeEnter: firstamGuard,
  },
  {
    path: "/insurance/firstamerican/title-001/landing",
    name: "insurance-firstamerican-title-001-landing",
    component: () => import("../views/insurance/firstamerican/InsuranceFirstamericanLanding.vue"),
    // 랜딩은 비로그인도 노출(가드 없음). '하러가기'(step-1)에서 firstamGuard 가 로그인 유도.
  },
  {
    path: "/insurance/firstamerican/title-001/reviews/:imageNo",
    name: "insurance-firstamerican-title-001-review-image",
    component: () =>
      import("../views/insurance/firstamerican/InsuranceFirstamericanReviewImage.vue"),
    // 랜딩 후기 이미지는 비로그인도 열 수 있어야 하므로 가드를 적용하지 않는다.
  },
  {
    path: "/insurance/firstamerican/title-001/register/step-1",
    name: "insurance-firstamerican-title-001-register-step-1",
    component: () =>
      import("../views/insurance/firstamerican/register/InsuranceFirstamericanRegisterStep1.vue"),
    beforeEnter: firstamGuard,
  },
  {
    path: "/insurance/firstamerican/title-001/register/step-2",
    name: "insurance-firstamerican-title-001-register-step-2",
    component: () =>
      import("../views/insurance/firstamerican/register/InsuranceFirstamericanRegisterStep2.vue"),
    beforeEnter: firstamGuard,
  },
  {
    path: "/insurance/firstamerican/title-001/register/step-3",
    name: "insurance-firstamerican-title-001-register-step-3",
    component: () =>
      import("../views/insurance/firstamerican/register/InsuranceFirstamericanRegisterStep3.vue"),
    beforeEnter: firstamGuard,
  },
  {
    path: "/insurance/firstamerican/title-001/register/step-4",
    name: "insurance-firstamerican-title-001-register-step-4",
    component: () =>
      import("../views/insurance/firstamerican/register/InsuranceFirstamericanRegisterStep4.vue"),
    beforeEnter: firstamGuard,
  },
  {
    path: "/insurance/firstamerican/title-001/register/step-5",
    name: "insurance-firstamerican-title-001-register-step-5",
    component: () =>
      import("../views/insurance/firstamerican/register/InsuranceFirstamericanRegisterStep5.vue"),
    beforeEnter: firstamGuard,
  },
  {
    path: "/insurance/firstamerican/title-001/register/step-6",
    name: "insurance-firstamerican-title-001-register-step-6",
    component: () =>
      import("../views/insurance/firstamerican/register/InsuranceFirstamericanRegisterStep6.vue"),
    beforeEnter: firstamGuard,
  },
  {
    path: "/insurance/firstamerican/title-001/register/step-7",
    name: "insurance-firstamerican-title-001-register-step-7",
    component: () =>
      import("../views/insurance/firstamerican/register/InsuranceFirstamericanRegisterStep7.vue"),
    beforeEnter: firstamGuard,
  },
  // 잔금일 미정 경로(NZSC-3816) — 매매 금액 구간 선택(step-4 대체 화면, 6단계 경로 전용)
  {
    path: "/insurance/firstamerican/title-001/register/amount-band",
    name: "insurance-firstamerican-title-001-register-amount-band",
    component: () =>
      import(
        "../views/insurance/firstamerican/register/InsuranceFirstamericanRegisterAmountBand.vue"
      ),
    beforeEnter: balanceUndecidedGuard,
  },
  // 잔금일 미정 경로(NZSC-3816) — 결제 후 계약 정보 입력(알리미 CTA + 알림톡 딥링크 진입)
  {
    path: "/insurance/firstamerican/title-001/contract-info",
    name: "insurance-firstamerican-title-001-contract-info",
    component: () =>
      import("../views/insurance/firstamerican/InsuranceFirstamericanContractInfo.vue"),
    beforeEnter: balanceUndecidedGuard,
  },
  {
    path: "/insurance/firstamerican/title-001/register/payment",
    name: "insurance-firstamerican-title-001-register-payment",
    component: () =>
      import("../views/insurance/firstamerican/register/InsuranceFirstamericanRegisterPayment.vue"),
    beforeEnter: firstamGuard,
  },
  {
    path: "/insurance/firstamerican/title-001/register/notice",
    name: "insurance-firstamerican-title-001-register-notice",
    component: () =>
      import("../views/insurance/firstamerican/register/InsuranceFirstamericanRegisterNotice.vue"),
    beforeEnter: firstamGuard,
  },
  {
    path: "/insurance/firstamerican/title-001/register/property",
    name: "insurance-firstamerican-title-001-register-property",
    component: () =>
      import(
        "../views/insurance/firstamerican/register/InsuranceFirstamericanRegisterProperty.vue"
      ),
    beforeEnter: firstamGuard,
  },
  {
    path: "/insurance/firstamerican/title-001/register/info",
    name: "insurance-firstamerican-title-001-register-info",
    component: () =>
      import("../views/insurance/firstamerican/register/InsuranceFirstamericanRegisterInfo.vue"),
    beforeEnter: firstamGuard,
  },
  {
    path: "/insurance/firstamerican/title-001/register/terms",
    name: "insurance-firstamerican-title-001-register-terms",
    component: () =>
      import("../views/insurance/firstamerican/register/InsuranceFirstamericanRegisterTerms.vue"),
    beforeEnter: firstamGuard,
  },
  {
    path: "/insurance/firstamerican/title-001/register/confirm",
    name: "insurance-firstamerican-title-001-register-confirm",
    component: () =>
      import("../views/insurance/firstamerican/register/InsuranceFirstamericanRegisterConfirm.vue"),
    beforeEnter: firstamGuard,
  },
  {
    path: "/insurance/firstamerican/title-001/register/complete",
    name: "insurance-firstamerican-title-001-register-complete",
    component: () =>
      import("../views/insurance/firstamerican/register/InsuranceFirstamericanComplete.vue"),
    // 결제 복귀 화면은 가드 미적용(QA 2026-06-12: 모바일 PG 복귀 컨텍스트에 토큰이 없을 수 있어
    // firstamGuard 가 로그인으로 보내버림). 결제 자체는 서버(initFirstamerican)가 게이트.
  },
  {
    path: "/insurance/firstamerican/title-001/modify",
    name: "insurance-firstamerican-title-001-modify",
    component: () => import("../views/insurance/firstamerican/InsuranceFirstamericanModify.vue"),
    beforeEnter: firstamGuard,
  },
];

export default insuranceRouter;
